<div class="flex flex-col gap-y-15">
  <div class="flex flex-col gap-y-5">
    <!-- 🔴 NO <h3> HERE. The shell prints the step's name above every step body but Funding,
         so this one rendered it TWICE -- and on a contribution both read "Allocate to Models",
         a line apart (AndyP, 2026-09-25). Four steps used to do this and the rest were cleaned
         up under ACMTECH-939; this one was missed because its own copy differed from the shell's
         on a distribution, which disguised the repeat as two related headings. -->
    <p class="text-sm text-gray">{{ lede() }}</p>

    @if (store.sleeveAmounts().rows.length === 0) {
      <!-- Not an error, and not empty-looking: an unsleeved account genuinely has no models to
           apportion across, and the money goes to the later steps instead. Saying so beats a
           blank table, which reads as a failed load. -->
      <div class="flex items-start px-10 py-8 mt-5 text-xs border rounded gap-x-8 border-mint-lighter bg-mint-lightest text-mint-darkest">
        <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
        <span>
          This account has no model sleeves.
          @if (store.isContribution()) {
            The contribution can be used to buy securities or set aside as cash on the next steps.
          } @else {
            The distribution is raised from the Custom Holdings sleeve on the next step.
          }
        </span>
      </div>
    } @else {
      <!-- The quick apply, matching the allocation step's control exactly: same `.button.is-sm`,
           same right-aligned placement, same mint/alert colour tracking the direction, and the
           same contract -- it applies what is REMAINING, so it works from any starting point and
           never overwrites what the other steps have already consumed.

           Hidden when there is nothing outstanding, as the allocation step's is. -->
      <div class="flex items-center justify-end mb-10 gap-x-10">
        @if (showApply()) {
          @if (applyDisabledReason(); as reason) {
            <span class="max-w-md text-xs text-right text-gray">{{ reason }}</span>
          }
          <!-- ⚠️ COLOUR FROM THE STORE, WORDS FROM THE COMPONENT, and the split is deliberate.
               The local `isOverCommitted()` says which DIRECTION the button moves money, so it
               still drives the label and the tooltip. `store.isOverCommitted()` says whether being
               over is a FAULT -- and on a distribution it is not, because the surplus stays in the
               account as cash. Sharing one signal painted a legitimate over-raise as an error on a
               step the advisor had not even touched. -->
          <button type="button" class="button is-sm"
            [class.is-mint]="!store.isOverCommitted()"
            [class.is-alert]="store.isOverCommitted()"
            [disabled]="!store.sleeveAmountsCanApplyOrionPercent()"
            [title]="isOverCommitted()
              ? 'Take the excess back out of the models, by the same percentages'
              : 'Spread what is left across the models by the account\'s standing percentages'"
            (click)="store.applyRemainingToSleeves()">
            {{ applyLabel() }}
          </button>
        }
        <button type="button" class="button is-sm is-mint is-inverted is-outlined"
          [disabled]="store.sleeveAmounts().total === 0"
          title="Set every model back to zero"
          (click)="store.clearSleeveAmounts()">
          Clear
        </button>
      </div>

      <div class="w-full overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="text-xs uppercase text-gray">
              <th class="p-8 text-left">Model</th>
              <th class="p-8 text-right">Current Value</th>
              <th class="p-8 text-right" [title]="orionColumnTooltip()">{{ orionColumnHeading() }}</th>
              <!-- ⚠️ No header over the amount column, deliberately. It read "Amount Out", which
                   was both jargon and mis-positioned: the cell is right-aligned and used to hold
                   a per-row ALL button after the input, so the caption landed over the BUTTON
                   rather than the box it was labelling.

                   The inputs need no caption — they carry a $ prefix and sit under a heading that
                   already says "Raise Cash from Models" — and the footer names the sum, which is
                   where the label actually earns its place. The ALL button is gone too: the
                   quick-apply above does the same job for every row at once, and "take all of
                   this one sleeve" was a shortcut nobody asked for. -->
              <th class="p-8"></th>
              <th class="p-8 text-right">% of Request</th>
            </tr>
          </thead>
          <tbody>
            @for (row of store.sleeveAmounts().rows; track row.sleeveKey) {
              <tr class="border-b border-gray-lightest">
                <td class="p-8">
                  <span class="font-semibold text-gray-darkest">{{ row.modelName }}</span>
                  <!-- Per-row, so a ten-sleeve account says WHICH row is wrong rather than
                       reporting one number at the foot. -->
                  @if (row.error; as error) {
                    <span class="block mt-2 text-xs font-semibold text-red-dark">{{ error }}</span>
                  }
                </td>
                <td class="p-8 text-right text-gray-darkest" style="font-variant-numeric: tabular-nums">
                  {{ row.currentValue | currency }}
                </td>
                <td class="p-8 text-right text-gray" style="font-variant-numeric: tabular-nums">
                  {{ row.orionPercent | number: '1.2-2' }}%
                </td>
                <td class="p-8 text-right">
                  <div class="flex items-center justify-end gap-x-5">
                    <!-- The $ sits outside the box for the reason given on the protected-cash
                         step: a number input cannot carry a prefix. -->
                    <span class="text-gray-darkest">$</span>
                    <input type="number" min="0" [value]="amountInput(row.amount)"
                      appSelectOnFocus appCommitOnEnter appTwoDecimals
                      class="w-120 text-right input is-sm no-spinner"
                      [class.is-error]="row.error !== null"
                      [attr.aria-label]="'Amount for ' + row.modelName"
                      (change)="onAmount(row.sleeveKey, $event)" />
                  </div>
                </td>
                <td class="p-8 text-right text-gray" style="font-variant-numeric: tabular-nums">
                  {{ row.percentOfFunding | number: '1.2-2' }}%
                </td>
              </tr>
            }
          </tbody>

          <tfoot>
            <tr class="font-semibold text-gray-darkest" style="background-color: #fafafa">
              <!-- The label spans the three context columns, so the figure below lands under the
                   INPUTS rather than floating a column away from them. -->
              <td class="p-8 border-t border-gray-lighter" colspan="3">
                {{ store.isContribution() ? 'Allocated' : 'Taken from models' }}
              </td>
              <td class="p-8 text-right border-t border-gray-lighter" style="font-variant-numeric: tabular-nums">
                {{ store.sleeveAmounts().total | currency }}
                <!-- The signed gap against the funding amount, in money. Immediate feedback at
                     the point of editing, which the strip above cannot give: it colours an
                     over-commitment red as the advisor types. -->
                @if (isOff(store.sleeveAmounts().delta)) {
                  <span class="block text-xs font-semibold" [class]="deltaClass(store.sleeveAmounts().delta)">
                    {{ deltaLabel(store.sleeveAmounts().delta) }}
                  </span>
                }
              </td>
              <td class="p-8 border-t border-gray-lighter"></td>
            </tr>
            <!-- ⚠️ The "Contribution amount $6,000" comparison row that used to sit here is gone.
                 The strip above states the amount as its leading context figure, so repeating it
                 under the table made three renderings of one number on screen at once — the
                 amount, the column total, and the gap between them, twice over.

                 The signed delta on the total above STAYS: it is immediate feedback at the point
                 of editing, which the strip cannot give, and it is what colours over-commitment
                 red as the advisor types. -->

          </tfoot>
        </table>
      </div>
    }

    <!-- One place, one message: the same computed that disables Next, so what the advisor reads
         and what blocks them cannot drift apart. -->
    @if (store.sleeveAmountsStepBlockedReason(); as reason) {
      <p class="flex items-center mt-5 text-sm font-semibold gap-x-5 text-red-dark" role="alert">
        <i class="material-icons" style="font-size: 1.8rem">warning</i>
        {{ reason }}
      </p>
    }
  </div>
</div>
